import { Box, Tab, Tabs, Tooltip } from '@mui/material'

import { useCategories } from '@/api/categories/categories'
import { getComposedSimulationName } from '@/api/open-ai/get-composed-simulation-name'
import { getComposedSimulationTopics } from '@/api/open-ai/get-composed-simulation-topics'
import microTrainingScreen from '@/assets/images/MobilePreview/MicroTrainingPreview/MicroTrainingScreen.png'
import MobilePhonePreview from '@/common/components/MobilePhonePreview'
import BrowserAppPreview from '@/common/components/MobilePhonePreview/BrowserAppPreview'
import SmsAppPreview from '@/common/components/MobilePhonePreview/SmsAppPreview'
import WhatsAppPreview from '@/common/components/MobilePhonePreview/WhatsAppPreview'
import { PhishingSimulationVector } from '@/types/phishingSimulations'
import { FC, SyntheticEvent, useEffect, useState } from 'react'
import { useAiSimulationContext } from '../state'
import MicroTrainingOutput from './MicroTrainingOutput'
import MonitorPreview from './MonitorPreview'
import MonitorPreviewIframe from './MonitorPreviewIframe'

const SummaryTabs: FC = () => {
  const [tabValue, setTabValue] = useState(0)
  const {
    bodyOutput,
    setSimulationName,
    landingPageOutput,
    setTopics,
    emailBody,
    subjectOutput,
    simulationType,
    vectorType,
    appName,
    senderName,
  } = useAiSimulationContext()
  const { data: categoryData } = useCategories()
  const { categories_by_tag } = categoryData || {}
  const [tags, setTags] = useState<string[]>([])
  const [isPreviewInFullscreen, setIsPreviewInFullscreen] = useState(false)

  useEffect(() => {
    const tags = Object.keys(categories_by_tag || [])
    setTags(tags)
  }, [categories_by_tag])

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }
  const setNewTopics = async () => {
    const generatedTopics = await getComposedSimulationTopics({
      subject: subjectOutput,
      simulationType: simulationType,
      vectorType: vectorType,
      simulationPrompt: emailBody,
      possibleTags: tags,
      tagCategories: categories_by_tag,
    })
    setTopics(
      generatedTopics
        .split(',')
        .map((topic: string) => topic.trim())
        .filter((topic: string) => tags.includes(topic)) || []
    )
  }
  const setNewName = async () => {
    const generatedName = await getComposedSimulationName({
      subject: subjectOutput,
      simulationType: simulationType,
      vectorType: vectorType,
      message: bodyOutput,
      landingPage: landingPageOutput,
    })
    setSimulationName(generatedName)
  }

  useEffect(() => {
    if (tags.length) {
      setNewTopics()
    }
  }, [tags])

  useEffect(() => {
    setNewName()
  }, [landingPageOutput])

  const TabContent = () => {
    switch (tabValue) {
      case 0:
        return (
          <Box>
            {vectorType === PhishingSimulationVector.Email ? (
              <MonitorPreview
                pageType="email"
                hasContent={true}
                isLoading={false}
                isFullscreen={isPreviewInFullscreen}
                setIsFullscreen={setIsPreviewInFullscreen}>
                <MonitorPreviewIframe text={bodyOutput} isModal={isPreviewInFullscreen} iFrameTitle="email-output" />
              </MonitorPreview>
            ) : vectorType === PhishingSimulationVector.SMS ? (
              //add fullscreen
              <MobilePhonePreview>
                <SmsAppPreview sender={appName || senderName} message={bodyOutput} />
              </MobilePhonePreview>
            ) : (
              //add fullscreen
              <MobilePhonePreview>
                <WhatsAppPreview sender={appName || senderName} message={bodyOutput} />
              </MobilePhonePreview>
            )}
          </Box>
        )
      case 1:
        return (
          <Box>
            {vectorType === PhishingSimulationVector.Email ? (
              <MonitorPreview
                pageType="landing"
                hasContent={true}
                isLoading={false}
                isFullscreen={isPreviewInFullscreen}
                setIsFullscreen={setIsPreviewInFullscreen}>
                <MonitorPreviewIframe
                  text={landingPageOutput}
                  isModal={isPreviewInFullscreen}
                  iFrameTitle="landing-page"
                />
              </MonitorPreview>
            ) : (
              //add fullscreen
              <MobilePhonePreview>
                <BrowserAppPreview fakeUrl={appName || senderName}>
                  <MonitorPreviewIframe text={landingPageOutput} isModal={false} iFrameTitle="landing-page" />
                </BrowserAppPreview>
              </MobilePhonePreview>
            )}
          </Box>
        )
      case 2:
        return (
          <Box>
            {vectorType === PhishingSimulationVector.Email ? (
              <MonitorPreview
                pageType="micro-training"
                hasContent={true}
                isLoading={false}
                isFullscreen={isPreviewInFullscreen}
                setIsFullscreen={setIsPreviewInFullscreen}>
                <MicroTrainingOutput />
              </MonitorPreview>
            ) : (
              //add fullscreen
              <MobilePhonePreview>
                <BrowserAppPreview fakeUrl={'micro-training'}>
                  <Box>
                    <Tooltip title="Full micro-training will be available to preview after saving">
                      <img src={microTrainingScreen} style={{ width: '100%', objectFit: 'cover' }}></img>
                    </Tooltip>
                  </Box>
                </BrowserAppPreview>
              </MobilePhonePreview>
            )}
          </Box>
        )
    }
  }
  return (
    <Box sx={{ margin: '0 auto' }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="summary tabs"
        sx={{
          width: 'fit-content',
          margin: '0 auto',
        }}>
        <Tab label="Template" />
        <Tab label="Landing Page" />
        <Tab label="Micro Training" />
      </Tabs>

      {<TabContent />}
    </Box>
  )
}

export default SummaryTabs
